import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, dateFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, Th, Td } from "@/components/common";
import { ListFilter, useListFilter } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ClipboardCheck, Truck } from "lucide-react";

export default function PickPack() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const canShip = can(user, "orders:ship");
  const [rows, setRows] = useState(null);

  const load = () => api.get("/orders?status=bestaetigt").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const ship = async (id) => {
    try { await api.post(`/orders/${id}/ship`); toast.success(t("pp.shipped")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filter = useListFilter(rows, { fields: ["order_number", "channel", "contact"], dateField: "ordered_at", storageKey: "pickpack" });
  const view = filter.filtered;

  return (
    <div>
      <PageHeader title="Pick & Pack" subtitle={t("pp.subtitle")} />
      <ListFilter filter={filter} testId="pickpack" withDates placeholder={t("filter.searchOrders")}
        exportName="pick_pack" exportColumns={[
          { label: t("common.order"), value: (o) => o.order_number },
          { label: t("ord.colChannel"), value: (o) => o.channel },
          { label: t("common.date"), value: (o) => dateFmt(o.ordered_at) },
          { label: t("ord.contactPerson"), value: (o) => o.contact || "" },
          { label: t("common.positions"), value: (o) => (o.lines || []).length },
          { label: t("common.quantity"), value: (o) => (o.lines || []).reduce((a, l) => a + (l.quantity || 0), 0) },
        ]} />
      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        view.length === 0 ? <EmptyState testId="pickpack-empty" icon={ClipboardCheck} title={t("pp.empty")} hint={t("pp.emptyHint")} /> : (
          <div className="space-y-3" data-testid="pickpack-list">
            {view.map((o) => (
              <div key={o.id} className="rounded-md border border-border bg-card p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="cursor-pointer" onClick={() => nav(`/auftraege/${o.id}`)}>
                    <div className="font-heading font-medium">{o.order_number} · {o.channel}</div>
                    <div className="text-xs text-muted-foreground">{dateFmt(o.ordered_at)} · {o.contact || "—"}</div>
                  </div>
                  {canShip && <Button size="sm" data-testid={`pick-ship-${o.order_number}`} onClick={() => ship(o.id)}><Truck className="h-4 w-4 mr-1.5" /> {t("pp.ship")}</Button>}
                </div>
                <table className="w-full"><thead><tr><Th>{t("common.item")}</Th><Th right>{t("common.quantity")}</Th></tr></thead>
                  <tbody>{o.lines.map((l, i) => <tr key={i}><Td>{l.name}{l.bundle_id && <span className="ml-1 text-xs text-primary">{t("pp.bundleComponents")}</span>}</Td><Td right mono>{l.quantity}</Td></tr>)}</tbody></table>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
